import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { balanceCheckOk, transformBalanceHistory } from '../components/dashboard/transformers';
import { useAuth } from '../context/AuthContext';
import useDashboardService from '../services/dashboard.service';
import { QUERY_KEYS } from '../utils/queryKeys';

/**
 * Available time ranges for historical data
 */
export type TimeRange = '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL';

/**
 * Calculate Unix timestamp for the start of a given time range
 */
export const getTimeRangeStart = (timeRange: TimeRange): number => {
    const now = Math.floor(Date.now() / 1000);
    const timeRangeMap: Record<TimeRange, number> = {
        '1W': now - 7 * 24 * 60 * 60,
        '1M': now - 30 * 24 * 60 * 60,
        '3M': now - 90 * 24 * 60 * 60,
        '6M': now - 180 * 24 * 60 * 60,
        '1Y': now - 365 * 24 * 60 * 60,
        ALL: 0,
    };
    return timeRangeMap[timeRange];
};

/**
 * Hook that provides all data needed for the dashboard
 * Centralizes data fetching, processing, and state management
 */
export const useDashboardData = () => {
    // ---------------------------------------------------------------------------
    // State and context
    // ---------------------------------------------------------------------------
    const { currentCtUser } = useAuth();
    const dashboardService = useDashboardService();
    const [timeRange, setTimeRange] = useState<TimeRange>('1M');
    const [selectedAssetSymbol, setSelectedAssetSymbol] = useState<string | null>(null);

    // Common timestamp for consistent time period across queries
    const thirtyDaysAgo = Math.floor(Date.now() / 1000) - 30 * 24 * 60 * 60;

    // ---------------------------------------------------------------------------
    // Base data queries
    // ---------------------------------------------------------------------------

    // Current portfolio balance and assets
    const { data: balanceData, isLoading: isBalanceDataLoading } = useQuery({
        queryKey: [QUERY_KEYS.DASHBOARD_BALANCE_DATA],
        queryFn: () => dashboardService.getBalanceData(thirtyDaysAgo, false),
        enabled: !!currentCtUser,
    });

    // Time-range specific portfolio history for charts
    const { data: timeRangePortfolioHistory, isLoading: isTimeRangeHistoryLoading } = useQuery({
        queryKey: [QUERY_KEYS.DASHBOARD_BALANCE_HISTORY, timeRange],
        queryFn: async () => {
            const response = await dashboardService.getPortfolioValueHistory(getTimeRangeStart(timeRange), false);
            return transformBalanceHistory(response);
        },
        enabled: !!currentCtUser,
    });

    // ---------------------------------------------------------------------------
    // Financial metrics queries
    // ---------------------------------------------------------------------------

    const dashboardOverviewResults = useQueries({
        queries: [
            {
                queryKey: [QUERY_KEYS.DASHBOARD_TOTAL_PROCEEDS],
                queryFn: () => dashboardService.getTotalProceeds(),
                enabled: !!currentCtUser,
            },
            {
                queryKey: [QUERY_KEYS.DASHBOARD_FIAT_INVESTED],
                queryFn: () => dashboardService.getFiatInvested(),
                enabled: !!currentCtUser,
            },
            {
                queryKey: [QUERY_KEYS.DASHBOARD_UNREALIZED_GAINS],
                queryFn: () => dashboardService.getUnrealizedGainsData(),
                enabled: !!currentCtUser,
            },
        ],
    });

    const [totalProceedsResult, fiatInvestedResult, unrealizedGainsResult] = dashboardOverviewResults;

    // ---------------------------------------------------------------------------
    // Asset-specific data query (for distribution modal)
    // ---------------------------------------------------------------------------

    const { data: assetDistribution, isLoading: isAssetDistributionLoading } = useQuery({
        queryKey: [QUERY_KEYS.DASHBOARD_COIN_BALANCE, selectedAssetSymbol],
        queryFn: async () => {
            if (!selectedAssetSymbol) return [];

            const data = await dashboardService.getCoinBalance(selectedAssetSymbol);
            return data.map((d) => ({
                exchangeName: d.exchangeName,
                quantity: d.quantity,
                imported_quantity: d.imported_quantity,
                unit_price: d.unit_price,
                value: d.value,
                percentageOfTotalValue: d.percentageOfTotalValue,
                hasBalanceDiscrepancy: !balanceCheckOk(d),
                allocationPercentage: d.percentageOfTotalValue,
            }));
        },
        enabled: !!currentCtUser && !!selectedAssetSymbol,
        // Don't fetch automatically when selectedAssetSymbol changes
    });

    // Function to load asset distribution data
    const loadAssetDistribution = (symbol: string) => {
        setSelectedAssetSymbol(symbol);
    };

    // ---------------------------------------------------------------------------
    // Derived data (computed from query results)
    // ---------------------------------------------------------------------------

    // Process asset data for table and pie chart
    const assetsData = useMemo(() => {
        if (!balanceData) return [];

        const assetsDataWithEmptyNames = balanceData.balances.map((balance) => ({
            ...balance,
            name: balance.name,
            symbol: balance.coin,
            displaySymbol: balance.symbol || balance.coin,
            coin: balance.coin,
            image: balance.image,
            quantity: balance.quantity,
            price: balance.unit_price,
            value: balance.value,
            percentageOfTotalValue: balance.percentageOfTotalValue,
            accountDistributions: [],
        }));

        return assetsDataWithEmptyNames.filter((asset) => asset.name && asset.name !== '');
    }, [balanceData]);

    // Calculate overview statistics (ROI, total return)
    const overviewData = useMemo(() => {
        const currentBalance = balanceData?.balance;
        const totalProceeds = totalProceedsResult.data;
        const fiatInvested = fiatInvestedResult.data;

        if (!currentBalance || !totalProceeds || !fiatInvested) return null;

        const fiatInvestedValue = fiatInvested.fiatInvested || 0;
        const fiatProceedsValue = totalProceeds.totalProceeds || 0;

        // Calculate totalReturn: fiatProceeds - fiatInvested + current balance
        const totalReturn = fiatProceedsValue - fiatInvestedValue + currentBalance;

        // Calculate ROI: ((balance - fiatInvested) / fiatInvested) * 100
        // Avoid division by zero
        let roi = 0;
        if (fiatInvestedValue > 0) {
            roi = ((currentBalance - fiatInvestedValue) / fiatInvestedValue) * 100;
        }

        return {
            totalReturn,
            roi,
        };
    }, [balanceData?.balance, totalProceedsResult.data, fiatInvestedResult.data]);

    // ---------------------------------------------------------------------------
    // Loading states
    // ---------------------------------------------------------------------------

    // Specific loading state for overview data
    const isOverviewDataLoading =
        !overviewData && (isBalanceDataLoading || totalProceedsResult.isLoading || fiatInvestedResult.isLoading);

    // Overall loading state
    const isLoading =
        isBalanceDataLoading ||
        isTimeRangeHistoryLoading ||
        isAssetDistributionLoading ||
        dashboardOverviewResults.some((r) => r.isLoading);

    // ---------------------------------------------------------------------------
    // Return consolidated dashboard data and state
    // ---------------------------------------------------------------------------
    return {
        // Balance data
        balanceData,
        isBalanceDataLoading,
        currentBalance: balanceData?.balance,

        // Assets data for table and allocation
        assetsData,

        // Time-range based chart data
        timeRangePortfolioHistory,
        isTimeRangeHistoryLoading,
        timeRange,
        setTimeRange,

        // Financial metrics
        totalProceeds: totalProceedsResult.data,
        isTotalProceedsLoading: totalProceedsResult.isLoading,
        fiatInvested: fiatInvestedResult.data,
        isFiatInvestedLoading: fiatInvestedResult.isLoading,
        unrealizedGains: unrealizedGainsResult.data,
        isUnrealizedGainsLoading: unrealizedGainsResult.isLoading,

        // Overview calculated data
        overviewData,
        isOverviewDataLoading,

        // Asset distribution data
        assetDistribution,
        isAssetDistributionLoading,
        loadAssetDistribution,
        selectedAssetSymbol,

        // Overall loading state
        isLoading,
    };
};
